import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { Component, type ErrorInfo, type FC, type ReactNode } from 'react';

import { Button } from '@craft/ui/components/buttons';
import { Stack } from '@craft/ui/components/structural';

import { logger } from '@/src/internal/logger';

const ErrorPageTemplate = dynamic(
  () => import('@/src/components/templates/ErrorPageTemplate').then(mod => mod.ErrorPageTemplate),
  { ssr: false },
);

interface ErrorBoundaryState {
  hasError: boolean;
}

type ErrorBoundaryCoreProps = {
  readonly children: ReactNode;
  readonly isLti: boolean;
  readonly onBack: () => void;
};

/**
 * Catches errors anywhere in the child component tree, logs those errors and displays a fallback
 * UI instead of the component tree that crashed.
 *
 * @see https://nextjs.org/docs/advanced-features/error-handling
 */
class ErrorBoundaryCore extends Component<ErrorBoundaryCoreProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryCoreProps) {
    super(props);
    this.state = { hasError: false };
  }

  /**
   * This function is used by React to update the state when an error is thrown in the child tree.
   * It is not explicitly called by the code.
   *
   * @see https://react.dev/reference/react/Component#catching-rendering-errors-with-an-error-boundary
   */
  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    logger.error(error, { ...errorInfo });
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <Stack marginTop='40vh' spacing={2}>
          <ErrorPageTemplate
            description='If the issue continues, please contact us using the button below.'
            isDirect
            title='Whoops, something went wrong.'
          />
          {!this.props.isLti && (
            <Stack direction='row' justifyContent='center'>
              <Button aria-label='go back button' onClick={this.props.onBack}>
                Go Back to Previous Page
              </Button>
            </Stack>
          )}
        </Stack>
      );
    }
    return this.props.children;
  }
}

interface ErrorBoundaryProps extends Omit<ErrorBoundaryCoreProps, 'onBack'> {}

/**
 * Catches errors anywhere in the child component tree, logs those errors and displays a fallback
 * UI instead of the component tree that crashed.
 *
 * The error boundary needs to behave differently if in LTI.
 */
export const ErrorBoundary: FC<ErrorBoundaryProps> = ({ children, ...props }) => {
  const router = useRouter();

  return (
    <ErrorBoundaryCore {...props} onBack={router.back}>
      {children}
    </ErrorBoundaryCore>
  );
};
